import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import SplitChars from '@/components/landing/split-chars'
import Sparkle from '@/components/landing/decor/sparkle'

interface SectionHeadingProps {
    index: string
    kicker: string
    title: string
    ja: string
    tone?: 'light' | 'dark'
    className?: string
    titleClassName?: string
}

// English display title with a numbered kicker and a Japanese subtitle.
// Sections reveal [data-char] letters and [data-heading-meta] lines with their own timelines.
const SectionHeading: FC<SectionHeadingProps> = ({
    index,
    kicker,
    title,
    ja,
    tone = 'light',
    className,
    titleClassName
}): ReactNode => {
    const dark = tone === 'dark'

    return (
        <div className={cn('flex flex-col gap-4', className)}>
            <p
                data-heading-meta
                className={cn(
                    'flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.32em]',
                    dark ? 'text-mist/70' : 'text-ink-soft'
                )}
            >
                <span>{index}</span>
                <span aria-hidden='true' className='h-px w-10 bg-current opacity-40' />
                <span>{kicker}</span>
            </p>

            <h2 className={cn('font-heading text-[clamp(3.25rem,10vw,9rem)] font-light leading-[0.88] tracking-[0.03em]', titleClassName)}>
                <SplitChars text={title} />
            </h2>

            <p
                data-heading-meta
                className={cn(
                    'flex items-center gap-3 text-sm tracking-[0.3em]',
                    dark ? 'text-mist/85' : 'text-ink'
                )}
            >
                <Sparkle className='size-3 shrink-0 text-gold' />
                <span>{ja}</span>
            </p>
        </div>
    )
}

export default SectionHeading
